import { apiErrorCode, apiErrorReason } from "@/lib/api";
import type {
  BondHistoryActorKind,
  BondHistoryEntry,
  BondSubmission,
} from "@/lib/brokerBonds";
import { messageFor } from "@/lib/errors";
import { workflowStatusLabel } from "@/lib/workflowStatus";
import {
  SUBMISSION_STATUSES_V2,
  readHistoryEvent,
  submissionStatusLabel,
} from "@/lib/bondStateModel";

// BRK-S323 — withdraw, start again, and the status history (consuming
// PMS-S718, ahead of the pin). Sprint 10 rule 21: a broker may withdraw before
// the bid bond is issued, or after a P&P request before the P&P bond is
// issued; a withdrawal cancels the order and it is never reopened; a resubmit
// is a NEW draft prefilled from it; the broker sees the status history.
//
// PMS decides every one of those things — `canWithdraw` says whether the
// button is offered, the withdraw's 2xx says it happened — so nothing here
// re-derives eligibility. Every reader is TOLERANT: an absent key hides the
// feature, a row it cannot read is dropped rather than rendered blank.

const ACTOR_KINDS: readonly BondHistoryActorKind[] = [
  "broker",
  "signal",
  "system",
];

/** A row the reader keeps — `actorKind` is null for a value it has not
 *  mirrored (the row still shows; only the actor is omitted). */
export type BondHistoryRow = Omit<BondHistoryEntry, "actorKind"> & {
  actorKind: BondHistoryActorKind | null;
};

const str = (v: unknown): string | null =>
  typeof v === "string" && v !== "" ? v : null;

/** The published history, newest first. Rows without a date or a `to` are
 *  dropped; an absent key is an empty history. */
export function readBondHistory(value: unknown): BondHistoryRow[] {
  if (!Array.isArray(value)) return [];
  const rows: BondHistoryRow[] = [];
  for (const raw of value as unknown[]) {
    if (typeof raw !== "object" || raw === null) continue;
    const r = raw as Record<string, unknown>;
    const at = str(r.at);
    const to = str(r.to);
    if (at === null || to === null) continue;
    const actor = r.actorKind;
    rows.push({
      at,
      from: str(r.from),
      to,
      reason: str(r.reason),
      actorKind: ACTOR_KINDS.find((k) => k === actor) ?? null,
      // An EVENT row (BRK-S400, PMS-S841); one not adopted reads as
      // null and the row still lists as its transition.
      event: readHistoryEvent(r.event),
    });
  }
  // ISO timestamps sort as text; newest first.
  return rows.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

/** The wireframe's Updated by column (BRK-S502): User / Signal / System. */
const ACTOR_LABELS: Record<BondHistoryActorKind, string> = {
  broker: "User",
  signal: "Signal",
  system: "System",
};

/** "User" / "Signal" / "System", or null for an actor this build has not
 *  mirrored. */
export function historyActorLabel(kind: unknown): string | null {
  return ACTOR_KINDS.find((k) => k === kind) !== undefined
    ? ACTOR_LABELS[kind as BondHistoryActorKind]
    : null;
}

/**
 * A history row's `reason` is a CLOSED code (PMS-S718, pinned at the sprint-10
 * release re-pin), never free text: the broker's own words when withdrawing are
 * the order's `withdrawReason`, not a history row's. So it is labelled here,
 * and a code this build has not mirrored renders NOTHING rather than the raw
 * code. No label is a decision reason (plan rule 25).
 */
const HISTORY_REASON_LABELS: Record<string, string> = {
  withdrawn: "Withdrawn by the broker",
  discarded: "Draft discarded",
  admin: "Changed by Signal",
  cancelled: "Cancelled",
  bid_won: "Bid won",
  bid_lost: "Bid lost",
  bid_result_corrected: "Bid result corrected",
  pp_first: "Performance and Payment requested without a bid bond",
};

/** A history row's reason in words, or null — never the raw code. */
export function historyReasonLabel(code: string | null): string | null {
  return code !== null && Object.hasOwn(HISTORY_REASON_LABELS, code)
    ? HISTORY_REASON_LABELS[code]
    : null;
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  submitted: "Filed with Signal",
  // A discarded draft; a withdrawal is `withdrawn` (BRK-S502).
  cancelled: "Cancelled",
};

/** A history row's status, in the portal's words (BRK-S447): the pinned
 *  draft / submitted / cancelled words, then every v2 submission status
 *  (`submissionStatusLabel`), else the lifecycle stage (`workflowStatusLabel`,
 *  "In progress" when unmirrored). */
export function historyStatusLabel(value: string): string {
  if (Object.hasOwn(STATUS_LABELS, value)) return STATUS_LABELS[value];
  if ((SUBMISSION_STATUSES_V2 as readonly string[]).includes(value)) {
    return submissionStatusLabel(value);
  }
  return workflowStatusLabel(value) ?? "In progress";
}

/** Withdrawn: the v2 `withdrawn` status (PMS-S837). `cancelled` is a
 *  discarded draft (BRK-S181) — the pinned "cancel + date" spelling of a
 *  withdrawal is gone (BRK-S502: no older PMS to read). */
export function isWithdrawn(order: Pick<BondSubmission, "status">): boolean {
  return order.status === "withdrawn";
}

export const WITHDRAW_NOT_ALLOWED_COPY =
  "This request can’t be withdrawn any more. Please message Signal.";

/** `details.reason` on a 409 WITHDRAW_NOT_ALLOWED (PMS-S718), in the
 *  portal's words. An unmirrored reason reads as the generic sentence. */
const WITHDRAW_REFUSAL_REASONS: Record<string, string> = {
  bond_issued:
    "This request can’t be withdrawn — its bond has already been issued. Please message Signal.",
  closed: "This request is closed, so there is nothing to withdraw.",
  has_decision:
    "This pre-approval already has a decision, so it can’t be withdrawn.",
};

export const CLONE_NOT_ALLOWED_COPY =
  "A new submission can’t be started from this one. Please start a new bond submission instead.";

/** A failed withdraw, in the portal's words. `WITHDRAW_NOT_ALLOWED` is read as
 *  a plain string: the pin does not publish it yet (PMS-S718). */
export function withdrawRefusal(err: unknown): string {
  if (apiErrorCode(err) === "WITHDRAW_NOT_ALLOWED") {
    const reason = apiErrorReason(err);
    return (
      (reason !== null && Object.hasOwn(WITHDRAW_REFUSAL_REASONS, reason)
        ? WITHDRAW_REFUSAL_REASONS[reason]
        : undefined) ?? WITHDRAW_NOT_ALLOWED_COPY
    );
  }
  return messageFor(err);
}

/** A failed clone (409 `CLONE_NOT_ALLOWED`, PMS-S718), in the portal's words. */
export function cloneRefusal(err: unknown): string {
  return apiErrorCode(err) === "CLONE_NOT_ALLOWED"
    ? CLONE_NOT_ALLOWED_COPY
    : messageFor(err);
}
